<template>
    <form ref="registerForm" class="customer-register-form flex flex-col" @submit.prevent="() => onSubmit()">
        <div class="flex flex-wrap gap-4">
            <div class="font-semibold">
                {{ $t('customer.registerForm.contactTitle') }}
            </div>

            <div v-if="businessAccountAllowed" class="form-control w-full">
                <div class="label">
                    <span class="label-text">{{ $t('customer.registerForm.accountTypeLabel') }}</span>
                </div>
                <div class="flex gap-6">
                    <label for="private-type" class="flex items-center cursor-pointer">
                        <input
                            id="private-type"
                            v-model="accountType"
                            type="radio"
                            name="accountType"
                            value="private"
                            class="radio radio-primary mr-2"
                        >
                        <span class="label-text">{{ $t('customer.registerForm.accountTypePrivate') }}</span>
                    </label>
                    <label for="business-type" class="flex items-center cursor-pointer">
                        <input
                            id="business-type"
                            v-model="accountType"
                            type="radio"
                            name="accountType"
                            value="business"
                            class="radio radio-primary mr-2"
                        >
                        <span class="label-text">{{ $t('customer.registerForm.accountTypeBusiness') }}</span>
                    </label>
                </div>
            </div>

            <div class="form-control w-full">
                <label for="register-email" class="label">
                    <span class="label-text">{{ $t('customer.registerForm.emailLabel') }}</span>
                </label>
                <input
                    id="register-email"
                    v-model="email"
                    required
                    type="email"
                    inputmode="email"
                    :placeholder="$t('customer.registerForm.emailPlaceholder')"
                    class="input input-bordered w-full"
                >
            </div>

            <div v-if="guestForm" class="form-control">
                <label class="label cursor-pointer">
                    <input
                        v-model="createAccount"
                        type="checkbox"
                        checked
                        class="checkbox mr-2"
                    >
                    <span class="label-text">{{ $t('customer.registerForm.createAccount') }}</span>
                </label>
            </div>

            <div v-if="createAccount" class="form-control w-full">
                <div class="grid grid-cols-2 gap-3 lg:gap-6">
                    <div class="col-span-2 lg:col-span-1">
                        <label for="register-password" class="label">
                            <span class="label-text">{{ $t('customer.registerForm.passwordLabel') }}</span>
                        </label>
                        <div class="w-full join">
                            <input
                                id="register-password"
                                ref="passwordInput"
                                v-model="password"
                                required
                                :type="showPassword ? 'text' : 'password'"
                                :placeholder="$t('customer.registerForm.passwordPlaceholder')"
                                :pattern="PASSWORD_PATTERN.source"
                                class="input input-bordered w-full border-r-0 join-item"
                                @input="validatePassword"
                            >
                            <div
                                class="btn btn-square btn-ghost border-l-0 border-border hover:border-border join-item"
                                tabindex="0"
                                role="button"
                                :aria-pressed="showPassword"
                                :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                                @click="showPassword = !showPassword"
                                @keydown.enter.prevent="showPassword = !showPassword"
                            >
                                <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                                <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                            </div>
                        </div>
                        <div class="label mt-1">
                            <span class="label-text-alt text-sm text-base-content-subtle font-normal leading-5">{{ $t('customer.registerForm.passwordLabel2') }}</span>
                        </div>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label for="register-password-repeat" class="label">
                            <span class="label-text">{{ $t('customer.registerForm.passwordRepeatLabel') }}</span>
                        </label>
                        <div class="join w-full">
                            <input
                                id="register-password-repeat"
                                ref="passwordRepeatInput"
                                v-model="passwordRepeat"
                                required
                                :type="showPasswordRepeat ? 'text' : 'password'"
                                :placeholder="$t('customer.registerForm.passwordRepeatPlaceholder')"
                                class="input input-bordered w-full border-r-0 join-item"
                            >
                            <div
                                tabindex="0"
                                role="button"
                                :aria-pressed="showPasswordRepeat"
                                :aria-label="showPasswordRepeat ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                                class="btn btn-square btn-ghost border-l-0 border-border hover:border-border join-item"
                                @click="showPasswordRepeat = !showPasswordRepeat"
                                @keydown.enter.prevent="showPasswordRepeat = !showPasswordRepeat"
                            >
                                <MiscIcon v-if="!showPasswordRepeat" name="eye" class="w-5 h-5" />
                                <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="accountType === 'business'" class="form-control w-full">
                <label for="register-vat-id" class="label">
                    <span class="label-text">{{ $t('customer.registerForm.vatIdLabel') }}</span>
                </label>
                <input
                    id="register-vat-id"
                    ref="vatIdInput"
                    v-model="vatId"
                    required
                    type="text"
                    :placeholder="$t('customer.registerForm.vatIdPlaceholder')"
                    class="input input-bordered w-full"
                    @input="validateVatId"
                >
            </div>

            <div v-if="createAccount" class="form-control w-full">
                <div class="col-span-2 lg:col-span-1">
                    <label for="register-dob" class="label">
                        <span class="label-text">{{ $t('customer.registerForm.dobLabel') }} <span class="text-base-content-subtle font-normal">({{ $t('customer.registerForm.optional') }})</span></span>
                    </label>
                    <div class="join w-full">
                        <input
                            id="register-dob"
                            ref="dobInput"
                            v-model="dateOfBirth"
                            type="date"
                            class="input input-bordered w-full border-r-0 join-item"
                        >
                        <div
                            aria-hidden="true"
                            class="btn btn-square btn-ghost border-l-0 border-border hover:border-border join-item"
                            @click="dobInput.showPicker()"
                            @keydown.enter.prevent="dobInput.showPicker()"
                        >
                            <MiscIcon name="calendar" class="w-5 h-5" />
                        </div>
                    </div>
                    <div class="label mt-1">
                        <span class="label-text-alt text-sm text-base-content-subtle font-normal leading-5">{{ $t('customer.registerForm.dobLabel2') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col">
            <div class="flex flex-wrap gap-4">
                <div class="font-semibold">
                    {{ $t('customer.registerForm.shippingTitle') }}
                </div>
                <CustomerAddressForm
                    id="registerShippingAddress"
                    v-model="shippingAddress"
                    type="shipping"
                    class="w-full"
                    :company-required="isBusinessAccount"
                    @is-pack-station="onPackStationChange"
                />
            </div>
        </div>

        <div v-show="alternateBillingAddress" class="flex flex-col">
            <div class="flex flex-wrap gap-4">
                <div class="font-semibold">
                    {{ $t('customer.registerForm.billingTitle') }}
                </div>
                <div class="w-full flex flex-col gap-3">
                    <label v-show="!isPackStation" for="same-billing-address" class="flex justify-between items-center cursor-pointer">
                        <input
                            id="same-billing-address"
                            v-model="billingSameAsShipping"
                            type="checkbox"
                            class="checkbox checkbox-secondary w-6 mr-4"
                        >
                        <span class="text-sm font-semibold mr-auto">{{ $t('customer.registerForm.billingSameAsShippingLabel') }}</span>
                    </label>

                    <div v-if="!billingSameAsShipping">
                        <CustomerAddressForm
                            id="registerBillingAddress"
                            v-model="billingAddress"
                            type="billing"
                            :company-required="isBusinessAccount"
                        />
                    </div>
                </div>
            </div>
        </div>

        <slot name="actions" :submit="onSubmit" :loading="loading" />
    </form>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { validateVatId as validateVatIdHelper } from '@/utils/vat-id-validation'
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()
const { PASSWORD_PATTERN, validatePassword: validatePasswordHelper } = usePasswordValidation()

type CustomerRegisterFormProps = {
    guestForm?: boolean
    alternateBillingAddress?: boolean
}

const props = withDefaults(defineProps<CustomerRegisterFormProps>(), {
    guestForm: false,
    alternateBillingAddress: true
})

const registerForm = ref()
const accountType = ref('private')
const vatId = ref('')
const vatIdInput = ref()
const email = ref('')
const password = ref('')
const passwordInput = ref()
const passwordRepeatInput = ref()
const passwordRepeat = ref('')
const dobInput = ref()
const dateOfBirth = ref('')
const createAccount = ref(!props.guestForm)
const billingSameAsShipping = ref(true)
const { sessionContext } = useSessionContext()
const { showNotification } = useNotification()

const shippingAddress: Ref<Partial<Schemas['CustomerAddress']>> = ref({
    salutationId: '',
    firstName: '',
    lastName: '',
    street: '',
    zipcode: '',
    city: '',
    countryId: sessionContext.value?.shippingLocation?.country?.id ?? '',
    phone: '',
    customerId: '',
    createdAt: ''
})
const billingAddress: Ref<Partial<Schemas['CustomerAddress']>> = ref({
    salutationId: '',
    firstName: '',
    lastName: '',
    street: '',
    zipcode: '',
    city: '',
    countryId: sessionContext.value?.shippingLocation?.country?.id ?? '',
    phone: '',
    customerId: '',
    createdAt: ''
})

const emit = defineEmits(['form-submitted'])
const { register } = useUser()
const { validateForm } = formValidation()
const loading = ref(false)
const showPassword = ref(false)
const showPasswordRepeat = ref(false)
const nuxtApp = useNuxtApp()
const { recaptcha } = useRecaptchaV3()

const { businessAccountAllowed } = useBusinessAccount()

const isBusinessAccount = computed(() => accountType.value === 'business')

async function onSubmit (callback?: CallableFunction): Promise<void> {
    loading.value = true

    const isNotABot = await recaptcha()
    if (!isNotABot) {
        console.error('Recaptcha failed, please try again.')
        showNotification(t('misc.recaptchaFailed'), 'error', true)
        loading.value = false
        return
    }

    if (createAccount.value) {
        if (password.value !== passwordRepeat.value) {
            passwordRepeatInput.value.setCustomValidity(t('customer.registerForm.passwordMismatch'))
        } else {
            passwordRepeatInput.value.setCustomValidity('')
        }
    }

    const isValid = await validateForm(registerForm.value)
    if (!isValid) {
        loading.value = false
        return
    }

    let dobObject = null
    if (dateOfBirth.value != null && dateOfBirth.value !== '') {
        dobObject = new Date(dateOfBirth.value)
    }

    try {
        const registerData = {
            ...(createAccount.value && dateOfBirth.value && { 
                ...(dobObject != null && { birthdayDay: dobObject.getDate() }),
                ...(dobObject != null && { birthdayMonth: dobObject.getMonth() + 1 }),
                ...(dobObject != null && { birthdayYear: dobObject.getFullYear() }),
            }),
            email: email.value,
            password: password.value,
            // salutationId: shippingAddress.value.salutationId,
            firstName: shippingAddress.value.firstName ?? '',
            lastName: shippingAddress.value.lastName ?? '',
            acceptedDataProtection: true,
            storefrontUrl: window.location.origin,
            billingAddress: (billingSameAsShipping.value
                ? shippingAddress.value
                : billingAddress.value) as Schemas['CustomerAddress'],
            ...(billingSameAsShipping.value ? {} : { shippingAddress: shippingAddress.value as Schemas['CustomerAddress'] }),
            guest: !createAccount.value,
            accountType: accountType.value as 'private' | 'business',
            ...(accountType.value === 'business' && vatId.value && { vatIds: [vatId.value] as [string] }),
        }

        await register(registerData)

        emit('form-submitted')
        nuxtApp.callHook('register')

        loading.value = false
        callback?.()
    } catch (e: unknown) {
        loading.value = false

        if (e instanceof ApiClientError) {
            const detail = e.details?.errors?.[0]?.detail
            showNotification(detail ?? e.message, 'error', true)
        } else {
            showNotification(e instanceof Error ? e.message : String(e), 'error', true)
        }
    }
}

function validateVatId () {
    validateVatIdHelper(vatIdInput.value, vatId.value, t('customer.registerForm.vatIdInvalid'))
}

function validatePassword () {
    validatePasswordHelper(passwordInput, password.value)
}

// Packstation adjustments to display and select checkbox to set alternate billing address
const isPackStation = ref(false)
watch(isPackStation, (value) => {
    billingSameAsShipping.value = value !== true
})
function onPackStationChange (e: CustomEvent) {
    isPackStation.value = e as unknown as boolean
}
</script>

<style lang="postcss">
#register-dob[type="date"]::-webkit-inner-spin-button,
#register-dob[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
}
</style>
